/**
 * Buchungsbestätigung als PDF herunterladen oder im Browser öffnen.
 */

import { API_BASE, ApiError } from "@/lib/apiClient";

export type BookingConfirmationPdfMode = "preview" | "download";

function buildPdfUrl(bookingId: string): string {
  const path = `/api/v1/bookings/${bookingId}/confirmation.pdf`;
  if (!API_BASE) return path;
  return `${API_BASE}${path}`;
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || fallback;
}

export function canShowBookingConfirmationPdf(status: string): boolean {
  return ["requested", "reserved", "confirmed", "checked_in", "checked_out"].includes(status);
}

export async function fetchBookingConfirmationPdf(
  bookingId: string,
  bookingNumber: string,
  mode: BookingConfirmationPdfMode,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(buildPdfUrl(bookingId), { credentials: "include" });
  } catch (err) {
    const message =
      err instanceof Error ? err.message : "PDF konnte nicht geladen werden.";
    throw new ApiError(0, message);
  }

  if (res.status === 401) {
    throw new ApiError(401, "Anmeldung erforderlich.");
  }

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
      try {
        const parsed = (await res.json()) as { detail?: unknown };
        if (typeof parsed.detail === "string") message = parsed.detail;
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, message);
  }

  const blob = await res.blob();
  const filename = filenameFromDisposition(
    res.headers.get("Content-Disposition"),
    `Buchungsbestaetigung-${bookingNumber}.pdf`,
  );
  const blobUrl = URL.createObjectURL(blob);

  if (mode === "preview") {
    const tab = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (!tab) {
      URL.revokeObjectURL(blobUrl);
      throw new ApiError(0, "Pop-up blockiert. Bitte Pop-ups erlauben oder „PDF speichern“ nutzen.");
    }
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return;
  }

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}
